<script setup>
import { WdsButtonGhost, WdsLinkupsEventCardDetailed, WdsLinkupsEventCardSimple, WdsOverlay, WdsPillActive, WdsPillFloating, WdsPillSelect } from '@wds/components';
import { buildCarouselCardInteractionTracking, mapEventKindToCarouselCardType, PDP_EVENT_KIND } from '@/composables/useEventsCarousel';

const props = defineProps({
  tag: {
    type: String,
    required: false,
    default: 'section',
  },
  eventCount: {
    type: Number,
    required: true,
  },
  eventList: {
    type: Array,
    required: true,
  },
  propertyData: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  eventUrl: {
    type: String,
    required: false,
    default: '',
  },
  showOgEventsModule: {
    type: Boolean,
    required: false,
    default: false,
  },
  showFilterPills: {
    type: Boolean,
    required: false,
    default: false,
  },
  activeEventFilter: {
    type: String,
    required: false,
    default: null,
  },
  ogEventsCount: {
    type: Number,
    required: false,
    default: 0,
  },
  linkupsCount: {
    type: Number,
    required: false,
    default: 0,
  },
  carouselDisplayed: {
    type: Boolean,
    required: false,
    default: false,
  },
  occasionGeniusEvents: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['filterClick']);

const nuxtApp = useNuxtApp();
const { t } = useI18n();
const { $tracking } = nuxtApp;
const router = useRouter();
const route = useRoute();
const { useUrl } = useUrls();

const { defineEventSchema } = generateSchemas();

const testingStore = useTestingStore();

const experimentLinkups = testingStore.hasEnabledFeature('web_linkupsCallouts');
const experimentPdpOgEvents = testingStore.hasEnabledFeature(
  'web_pdpOGEventsCarousel',
);

const linkupsContainer = ref(null);
const linkupsCarousel = ref(null);
const linkupsCarouselDetailed = ref(null);
const detailedCardEls = [];
const ogMoreDetailsTargets = shallowRef({});
const ogCtaTargets = shallowRef({});

const linkupsCarouselBoundaries = ref({
  left: true,
  right: false,
});
const linkupsCarouselHasOverflow = ref(false);
const showLinkupsOverlay = ref(false);
const activeLinkupInGallery = ref(0);
const bodyScrollTop = ref(0);

const commonTrackingParameters = computed(() => ({
  linkups_web_cohort: experimentLinkups ? 'variation' : 'control',
  linkups_counter_flag: experimentLinkups,
  linkups_carousel_flag: experimentLinkups,
  linkups_count_displayed: props.linkupsCount,
  og_events_carousel_flag: props.showOgEventsModule,
  og_events_count: props.showOgEventsModule ? props.ogEventsCount : 0,
  events_count_displayed: props.eventCount || 0,
  og_events_web_cohort: experimentPdpOgEvents ? 'variation' : 'control',
  carousel_flag: props.carouselDisplayed,
  og_events_count_display: props.showOgEventsModule ? props.ogEventsCount : 0,
}));

const trackedEventsFromCarousel = [];

const getEventsForCarousel = computed(() => props.eventList || []);

const hasCarouselEvents = computed(() => getEventsForCarousel.value.length > 0);

const hasMultipleGalleryEvents = computed(
  () => getEventsForCarousel.value.length > 1,
);

const showSimpleCarouselNavigation = computed(
  () => hasCarouselEvents.value && linkupsCarouselHasOverflow.value,
);

const isFilteredEmptyState = computed(() => {
  return (
    props.showFilterPills && props.activeEventFilter && !hasCarouselEvents.value
  );
});

const shouldShowCarouselSection = computed(() => {
  return hasCarouselEvents.value || isFilteredEmptyState.value;
});

const filteredEmptyStateMessage = computed(() => {
  if (props.activeEventFilter === PDP_EVENT_KIND.HW_LINKUP) {
    return t('t_PDPEVENTSEMPTYHOSTEL');
  }

  if (props.activeEventFilter === PDP_EVENT_KIND.OG_EVENT) {
    return t('t_PDPEVENTSEMPTYCITY');
  }

  return '';
});

const linkupsGalleryAtLeftBoundary = computed(() => {
  return activeLinkupInGallery.value <= 0;
});

const linkupsGalleryAtRightBoundary = computed(() => {
  return activeLinkupInGallery.value >= getEventsForCarousel.value.length - 1;
});

const eventSchema = computed(() =>
  defineEventSchema(getEventsForCarousel.value),
);

const carouselSectionTitle = computed(() => {
  if (props.showOgEventsModule) {
    return t('t_PDPEVENTSCAROUSELTITLE', { LOCATIONNAME: props?.propertyData?.city?.name });
  }

  return t('t_LINKUPEVENTS');
});

const carouselSectionSubtitle = computed(() => {
  if (props.showOgEventsModule) {
    return t('t_PDPEVENTSCAROUSELSUBTITLE');
  }

  return t('t_LINKUPBOOKNOWTOJOIN');
});

const cityEventsPageUrl = computed(() => {
  const {
    continent,
    country,
    city,
  } = props.propertyData ?? {};

  if (!continent || !country || !city) {
    return null;
  }

  return useUrl.getEventsCityPageUrl({
    rootSlug: t('t_OGEVENTSSLUG'),
    urlFriendlyContinent: continent?.urlFriendlyName,
    urlFriendlyCountry: country?.urlFriendlyName,
    urlFriendlyCity: city?.urlFriendlyName,
  });
});

const shouldShowViewMoreButton = computed(() => {
  return (
    cityEventsPageUrl.value
    && props.showOgEventsModule
    && props.ogEventsCount > 0
    && (props.activeEventFilter === null || props.activeEventFilter === PDP_EVENT_KIND.OG_EVENT)
  );
});

const DEFAULT_LINKUP_CARD_GAP_PX = 0;

function getLinkupCardGapPx(element) {
  if (!element) {
    return DEFAULT_LINKUP_CARD_GAP_PX;
  }

  const parsedGap = Number.parseInt(getComputedStyle(element).marginRight, 10);
  return Number.isFinite(parsedGap) ? parsedGap : DEFAULT_LINKUP_CARD_GAP_PX;
}

function getLinkupEventCardFullWidth() {
  const linkupsCarouselDetailedRef = toValue(linkupsCarouselDetailed);
  if (!linkupsCarouselDetailedRef) {
    return null;
  }

  const linkupsFirstCard = linkupsCarouselDetailedRef.firstElementChild;
  if (!linkupsFirstCard) {
    return 0;
  }

  const cardWidth = linkupsFirstCard.offsetWidth + getLinkupCardGapPx(linkupsFirstCard);
  return Number.isFinite(cardWidth) ? cardWidth : 0;
}

function handleLinkupEventCardSwipe() {
  if (!trackedEventsFromCarousel.includes(activeLinkupInGallery.value)) {
    $tracking.triggerLinkupEventAction({
      action: 'Linkups Reel Swiped',
      ...commonTrackingParameters.value,
      linkups_gallery_reels_count: getEventsForCarousel.value?.length,
      linkups_gallery_position_viewed: activeLinkupInGallery.value,
    });
    trackedEventsFromCarousel.push(activeLinkupInGallery.value);
  }
}

function updateSimpleCarouselOverflow() {
  const linkupsCarouselRef = toValue(linkupsCarousel);
  if (!linkupsCarouselRef) {
    linkupsCarouselHasOverflow.value = false;
    return;
  }

  linkupsCarouselHasOverflow.value
    = linkupsCarouselRef.scrollWidth > linkupsCarouselRef.clientWidth + 1;
}

async function handleLinkupsSectionScroll(e) {
  const linkupsCarouselRef = toValue(linkupsCarousel);
  if (linkupsCarouselRef) {
    linkupsCarouselBoundaries.value.left = e.currentTarget?.scrollLeft === 0;
    linkupsCarouselBoundaries.value.right
      = e.currentTarget?.scrollLeft
        === linkupsCarouselRef?.scrollWidth - linkupsCarouselRef?.offsetWidth;
  }
}

async function handleLinkupsGalleryScroll(e) {
  const scrollEnd = e.currentTarget.scrollLeft;
  const cardWidth = getLinkupEventCardFullWidth();
  if (cardWidth) {
    activeLinkupInGallery.value = Math.round(scrollEnd / cardWidth);
    handleLinkupEventCardSwipe();
  }
}

function setDetailedCardRef(idx, el) {
  detailedCardEls[idx] = el || undefined;
}

function resolveOgMoreDetailsTarget(idx) {
  const wrapper = detailedCardEls[idx];
  if (!wrapper) {
    return null;
  }

  return wrapper.querySelector('.details-section.text-details')
    || wrapper.querySelector('.details-body');
}

function resolveOgCtaTarget(idx) {
  const wrapper = detailedCardEls[idx];
  if (!wrapper) {
    return null;
  }

  return wrapper.querySelector('.details-body');
}

function bindOgExpandedCardTargets() {
  const nextMoreDetailsTargets = {};
  const nextCtaTargets = {};

  getEventsForCarousel.value.forEach((eventItem, idx) => {
    if (!eventItem?.isOgEvent) {
      return;
    }

    if (eventItem.moreDetailsUrl) {
      const moreDetailsTarget = resolveOgMoreDetailsTarget(idx);
      if (moreDetailsTarget) {
        nextMoreDetailsTargets[idx] = moreDetailsTarget;
      }
    }

    const ctaTarget = resolveOgCtaTarget(idx);
    if (ctaTarget) {
      nextCtaTargets[idx] = ctaTarget;
    }
  });

  ogMoreDetailsTargets.value = nextMoreDetailsTargets;
  ogCtaTargets.value = nextCtaTargets;
}

async function openLinkupEventsDetails() {
  bodyScrollTop.value = window.scrollY;
  showLinkupsOverlay.value = true;
  await nextTick();

  bindOgExpandedCardTargets();
  linkupDetailedEventMoveToActive('instant');

  setTimeout(() => {
    linkupsCarouselDetailed?.value?.addEventListener(
      'scrollend',
      handleLinkupsGalleryScroll,
      false,
    );
  }, 100);
}

async function closeLinkupEventsDetails() {
  linkupsCarouselDetailed?.value?.removeEventListener(
    'scrollend',
    handleLinkupsGalleryScroll,
    false,
  );

  ogMoreDetailsTargets.value = {};
  ogCtaTargets.value = {};
  showLinkupsOverlay.value = false;

  await router.replace({
    hash: '',
    query: route.query,
  });
  await nextTick();

  setTimeout(() => window.scrollTo(0, bodyScrollTop.value), 0);
}

function handleLinkupEventCardClick(eventPos) {
  activeLinkupInGallery.value = Number.isInteger(eventPos) ? eventPos : 0;
  openLinkupEventsDetails();

  const clickedEvent = getEventsForCarousel.value[activeLinkupInGallery.value];

  $tracking.triggerLinkupEventAction({
    action: 'Carousel Clicked',
    ...commonTrackingParameters.value,
    ...buildCarouselCardInteractionTracking(
      clickedEvent,
      activeLinkupInGallery.value,
    ),
    linkups_carousel_position_clicked: activeLinkupInGallery.value,
    linkups_carousel_count: getEventsForCarousel.value?.length,
  });
}

function handleLinkupEventReadMoreClick(eventPos) {
  const clickedEvent = getEventsForCarousel.value[eventPos];

  activeLinkupInGallery.value = eventPos;
  linkupDetailedEventMoveToActive();

  $tracking.triggerLinkupEventAction({
    action: 'Carousel Read More Clicked',
    ...commonTrackingParameters.value,
    ...buildCarouselCardInteractionTracking(clickedEvent, eventPos),
    linkups_gallery_position_clicked: Number.isInteger(eventPos)
      ? eventPos
      : null,
  });

  nextTick(() => bindOgExpandedCardTargets());
}

async function handleLinkupEventBookNowClick(eventPos) {
  const clickedEvent = getEventsForCarousel.value[eventPos];

  $tracking.triggerLinkupEventAction({
    action: 'Carousel Book Now Clicked',
    ...commonTrackingParameters.value,
    ...buildCarouselCardInteractionTracking(clickedEvent, eventPos),
    linkups_gallery_position_clicked: Number.isInteger(eventPos)
      ? eventPos
      : null,
  });

  if (clickedEvent?.isOgEvent) {
    closeLinkupEventsDetails();
    nuxtApp.callHook('property-availability:open');
    return;
  }

  if (clickedEvent?.searchDatesUrl) {
    closeLinkupEventsDetails();
    await navigateTo(clickedEvent.searchDatesUrl, { external: true });
    return;
  }

  closeLinkupEventsDetails();
  nuxtApp.callHook('property-availability:open');
}

function handleOgEventMoreDetailsClick(eventPos) {
  const clickedEvent = getEventsForCarousel.value[eventPos];
  if (!clickedEvent?.isOgEvent || !clickedEvent.moreDetailsUrl) {
    return;
  }

  $tracking.triggerLinkupEventAction({
    action: 'Carousel More Details Clicked',
    ...commonTrackingParameters.value,
    ...buildCarouselCardInteractionTracking(clickedEvent, eventPos),
    linkups_gallery_position_clicked: Number.isInteger(eventPos)
      ? eventPos
      : null,
  });
}

function handleFilterPillClick(kind) {
  const isOgFilterPill = kind === PDP_EVENT_KIND.OG_EVENT;

  $tracking.triggerLinkupEventAction({
    action: 'Carousel Filter Click',
    ...commonTrackingParameters.value,
    event_filter_kind: kind,
    event_filter_active: props.activeEventFilter !== kind,
    carousel_filter: mapEventKindToCarouselCardType(kind),
    ...(isOgFilterPill && props.occasionGeniusEvents
      ? { occasion_genius_events: props.occasionGeniusEvents }
      : {}),
  });

  emit('filterClick', kind);
}

function linkupSimpleEventsScroll(direction) {
  const validDirection = [1, -1].includes(direction) ? direction : 1;
  const linkupsCarouselRef = toValue(linkupsCarousel);
  if (!linkupsCarouselRef) {
    return;
  }

  const linkupsFirstCard = linkupsCarouselRef.firstElementChild;
  if (!linkupsFirstCard) {
    return;
  }

  const slideOffset
    = (linkupsFirstCard.offsetWidth + getLinkupCardGapPx(linkupsFirstCard)) * validDirection;

  if (!Number.isFinite(slideOffset)) {
    return;
  }

  linkupsCarouselRef.scrollBy({
    left: slideOffset,
    top: 0,
    behavior: 'smooth',
  });
}

function linkupDetailedEventsScroll(direction) {
  const validDirection = [1, -1].includes(direction) ? direction : 1;
  const linkupsCarouselDetailedRef = toValue(linkupsCarouselDetailed);

  if (
    (validDirection < 0 && linkupsGalleryAtLeftBoundary.value)
    || (validDirection > 0 && linkupsGalleryAtRightBoundary.value)
  ) {
    return;
  }

  if (linkupsCarouselDetailedRef) {
    activeLinkupInGallery.value += validDirection;
    linkupDetailedEventMoveToActive();
    handleLinkupEventCardSwipe();
  }
}

function linkupDetailedEventMoveToActive(behaviour = 'smooth') {
  const linkupsCarouselDetailedRef = toValue(linkupsCarouselDetailed);
  if (linkupsCarouselDetailedRef) {
    const linkupsGalleryCards = linkupsCarouselDetailedRef.querySelectorAll(
      '.linkups-carousel-detailed-card',
    );
    const nextCard = linkupsGalleryCards?.[activeLinkupInGallery.value];
    if (nextCard) {
      nextCard?.scrollIntoView({
        behavior: behaviour,
        inline: 'center',
      });
    }
  }
}

useSchemaOrg([eventSchema]);

watch(
  () => route.hash,
  (newHash, oldHash) => {
    if (oldHash === '#linkups' && newHash === '') {
      closeLinkupEventsDetails();
    }
  },
);

watch(
  getEventsForCarousel,
  async () => {
    await nextTick();
    updateSimpleCarouselOverflow();
  },
  { flush: 'post' },
);

onMounted(() => {
  updateSimpleCarouselOverflow();
  window.addEventListener('resize', updateSimpleCarouselOverflow, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSimpleCarouselOverflow);
});
</script>

<template>
  <div
    v-if="shouldShowCarouselSection"
    id="linkups"
    ref="linkupsContainer"
    class="linkups-container"
  >
    <component :is="tag" class="linkups-carousel-simple">
      <h2>{{ carouselSectionTitle }}</h2>
      <span class="linkups-subtitle">{{ carouselSectionSubtitle }}</span>
      <div v-if="showFilterPills" class="linkups-filter-pills">
        <component
          :is="
            activeEventFilter === PDP_EVENT_KIND.HW_LINKUP
              ? WdsPillActive
              : WdsPillSelect
          "
          :text="$t('t_PDPEVENTSFILTERHOSTEL')"
          @click="handleFilterPillClick(PDP_EVENT_KIND.HW_LINKUP)"
        />
        <component
          :is="
            activeEventFilter === PDP_EVENT_KIND.OG_EVENT
              ? WdsPillActive
              : WdsPillSelect
          "
          :text="$t('t_PDPEVENTSFILTEROG')"
          @click="handleFilterPillClick(PDP_EVENT_KIND.OG_EVENT)"
        />
      </div>
      <div v-if="showSimpleCarouselNavigation" class="linkups-navigation">
        <WdsPillFloating
          icon-start="arrow-left"
          aria-label="left arrow"
          :disabled="linkupsCarouselBoundaries.left"
          @click="linkupSimpleEventsScroll(-1)"
        />
        <WdsPillFloating
          icon-start="arrow-right"
          aria-label="right arrow"
          :disabled="linkupsCarouselBoundaries.right"
          @click="linkupSimpleEventsScroll(1)"
        />
      </div>
      <p v-if="isFilteredEmptyState" class="linkups-empty-state">
        {{ filteredEmptyStateMessage }}
      </p>
      <div
        v-else
        ref="linkupsCarousel"
        class="linkups-carousel"
        @scroll="handleLinkupsSectionScroll"
      >
        <WdsLinkupsEventCardSimple
          v-for="(eventItem, idx) in getEventsForCarousel"
          :key="`event${eventItem.id}${eventItem.kind}`"
          :image="eventItem.image.small"
          :title="eventItem.name"
          :date="eventItem.date"
          class="linkups-carousel-card"
          href="#linkups"
          @click="handleLinkupEventCardClick(idx)"
        />
      </div>
      <WdsButtonGhost
        v-if="shouldShowViewMoreButton"
        :href="cityEventsPageUrl"
        class="linkups-view-more-button"
        :text="$t('t_VIEWMORE')"
        icon-end="arrow-right"
      />
    </component>
    <WdsOverlay
      v-if="showLinkupsOverlay"
      class="linkups-overlay"
      :jump-to-top-on-close="false"
      @mousedown="closeLinkupEventsDetails"
    >
      <div
        v-if="hasMultipleGalleryEvents"
        class="linkups-carousel-detailed-navigation nav-left"
      >
        <WdsPillFloating
          icon-start="arrow-left"
          aria-label="left arrow"
          :disabled="linkupsGalleryAtLeftBoundary"
          @click="linkupDetailedEventsScroll(-1)"
          @mousedown.stop
        />
      </div>
      <div
        v-if="hasMultipleGalleryEvents"
        class="linkups-carousel-detailed-navigation nav-right"
      >
        <WdsPillFloating
          icon-start="arrow-right"
          aria-label="right arrow"
          :disabled="linkupsGalleryAtRightBoundary"
          @click="linkupDetailedEventsScroll(1)"
          @mousedown.stop
        />
      </div>
      <div ref="linkupsCarouselDetailed" class="linkups-carousel-detailed">
        <div
          v-for="(eventItem, idx) in getEventsForCarousel"
          :key="`eventDetail${eventItem.id}${eventItem.kind}`"
          :ref="(el) => setDetailedCardRef(idx, el)"
          class="linkups-carousel-detailed-card"
        >
          <WdsLinkupsEventCardDetailed
            :event-id="eventItem.id"
            :event-type="eventItem?.type"
            :event-image="eventItem?.image?.large"
            :event-title="eventItem?.name"
            :event-date="eventItem?.date"
            :event-price="eventItem?.price"
            :event-host-name="eventItem?.host?.name"
            :event-host-avatar="eventItem?.host?.image"
            :event-location="eventItem?.location?.city"
            :event-audience="eventItem?.audience"
            :event-description="eventItem?.description"
            :event-additional-info="eventItem?.additionalInfo"
            :translations="eventItem?.translations"
            @close-linkup-sheet="closeLinkupEventsDetails"
            @book-now-click="handleLinkupEventBookNowClick(idx)"
            @show-more-details="handleLinkupEventReadMoreClick(idx)"
            @mousedown.stop
          />
          <Teleport
            :disabled="!ogMoreDetailsTargets[idx]"
            :to="ogMoreDetailsTargets[idx] || 'body'"
          >
            <div
              v-if="eventItem.isOgEvent && eventItem.moreDetailsUrl"
              class="og-event-more-details-row"
            >
              <a
                class="og-event-more-details text-link underline"
                :href="eventItem.moreDetailsUrl"
                target="_blank"
                rel="noopener noreferrer"
                @mousedown.stop
                @click.stop="handleOgEventMoreDetailsClick(idx)"
              >
                {{ $t('t_PDPEVENTSMOREDETAILS') }}
              </a>
            </div>
          </Teleport>
          <Teleport
            :disabled="!ogCtaTargets[idx]"
            :to="ogCtaTargets[idx] || 'body'"
          >
            <WdsButtonGhost
              v-if="eventItem.isOgEvent"
              class="og-event-book-bed"
              :text="eventItem.translations?.tBookNow || $t('t_BOOKABED')"
              icon-end="arrow-right"
              full-width
              @mousedown.stop
              @click.stop="handleLinkupEventBookNowClick(idx)"
            />
          </Teleport>
        </div>
      </div>
    </WdsOverlay>
  </div>
</template>

<style scoped lang="scss">
.linkups-container {
  .linkups-carousel-simple {
    position: relative;
    color: $wds-color-purple;
    background-color: $wds-color-purple-lightest;
    border-radius: $wds-spacing-l;
    padding-inline: $wds-spacing-m;
    padding-block: $wds-spacing-m $wds-spacing-s;

    .linkups-subtitle {
      @include body-2-reg;
    }

    .linkups-filter-pills {
      display: flex;
      flex-wrap: wrap;
      gap: $wds-spacing-s;
      margin-block: $wds-spacing-s;
    }

    .linkups-empty-state {
      @include body-2-reg;

      margin-block: $wds-spacing-s 0;
    }

    .linkups-navigation {
      display: none;
      position: absolute;
      top: $wds-spacing-m;
      right: $wds-spacing-m;
    }

    .linkups-carousel {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      overflow-x: auto;

      @include no-scrollbar;

      .linkups-carousel-card {
        margin: $wds-spacing-s $wds-spacing-m $wds-spacing-s 0;
        cursor: pointer;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .linkups-view-more-button {
      display: flex;
      width: 100%;
      margin-top: $wds-spacing-s;
    }

    @include tablet-large {
      .linkups-navigation {
        display: flex;
        gap: $wds-spacing-m;
      }

      .linkups-view-more-button {
        margin-left: auto;
        width: fit-content;
      }
    }
  }

  .linkups-overlay {
    .linkups-carousel-detailed-navigation {
      display: none;
    }

    .linkups-carousel-detailed {
      position: relative;
      max-height: 600px;
      height: 100vh;
      width: 100vw;
      padding-inline: 50%;
      display: flex;
      overflow: auto hidden;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;

      @include no-scrollbar;

      .linkups-carousel-detailed-card {
        margin: 0 $wds-spacing-l $wds-spacing-s 0;
        min-width: min(90vw, wds-rem(480px));
        scroll-snap-align: center;
        display: flex;
        flex-direction: column;

        &:last-child {
          margin-right: 0;
        }

        .og-event-more-details-row {
          display: block;
          width: 100%;
          margin-top: $wds-spacing-xs;
        }

        .og-event-more-details {
          @include body-1-reg;

          color: $wds-color-ink-darker;
          text-decoration: underline;

          &:hover,
          &:focus,
          &:visited {
            color: $wds-color-ink-darker;
            text-decoration: underline;
          }
        }

        .og-event-book-bed {
          width: 100%;
          margin-top: $wds-spacing-s;
        }
      }
    }

    @include desktop {
      .linkups-carousel-detailed-navigation {
        display: flex;
        position: absolute;
        top: calc(50% - 20px);
        z-index: 700;

        &.nav-left {
          left: calc(50% - 20px - 240px);
        }

        &.nav-right {
          right: calc(50% - 20px - 240px);
        }
      }

      .linkups-carousel-detailed {
        padding-inline: calc(50% - 240px);

        .linkups-carousel-detailed-card {
          margin-right: wds-rem(72px);
          min-width: wds-rem(480px);
        }
      }
    }
  }
}
</style>
